import { apiEndpoints } from '@/apiEndpoints';
import { SharedAgency } from '@/types/distributors/agencySelectionColumnDef';
import { PublishedCircuit } from '@/types/distributors/CircuitSelectionColumnDef';
import { Distributor } from '@/types/distributors/distributorColumnDef';
import {
  Agency,
  NetworkWithAgencies,
} from '@/types/distributors/networkColumnDef';
import { ReceivedPartnershipRequest } from '@/types/distributors/ReceivedPartnershipRequestsColumnDef';
import axios from 'axios';
import { PartnershipRequestsParam } from './receiver.api';

interface DistributorsResponse {
  total: number;
  countNewAgencies: number;
  countPartnerships: number;
  distributors: Distributor[];
}

export interface DistributorParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, string>;
}

export const getDistributors = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
  filters = {},
}: DistributorParams) => {
  const filtersStr = Object.entries(filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');
  const result = await axios.get<DistributorsResponse>(
    apiEndpoints.distributors.base,
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
        filters: filtersStr,
      },
    },
  );
  return result.data;
};

export type DistributorFilterResponse = { id: string; name: string }[];

export const getDistributorsNetworks = async () => {
  const result = await axios.get<DistributorFilterResponse>(
    apiEndpoints.distributors.networks,
  );
  return result.data;
};

export type CountriesResponse = { country: string }[];

export const getDistributorsCountries = async () => {
  const result = await axios.get<CountriesResponse>(
    apiEndpoints.distributors.countries,
  );
  return result.data;
};

export const getDistributorsCircuits = async () => {
  const result = await axios.get<DistributorFilterResponse>(
    apiEndpoints.distributors.circuits,
  );
  return result.data;
};

export type AllNetworksResponse = {
  networks: NetworkWithAgencies[];
  countNewAgencies: number;
};

export type NetworkParams = {
  networkId?: string;
  networkName?: string;
};

export const getAllNetworks = async (params: NetworkParams) => {
  const result = await axios.get<AllNetworksResponse>(
    apiEndpoints.distributors.allNetworks,
    {
      params: {
        networkId: params.networkId,
        networkName: params.networkName
          ? encodeURIComponent(params.networkName)
          : undefined,
      },
    },
  );
  return result.data;
};

export type CircuitsForAgenciesResponse = {
  circuits: PublishedCircuit[];
  agencies: string[];
};

export const getCircuitsForAgencies = async (
  agencies: string[],
  networks: string[] = [],
) => {
  const result = await axios.post<CircuitsForAgenciesResponse>(
    apiEndpoints.distributors.circuits,
    { agencies, networks },
  );
  return result.data;
};

export type ShareCircuitsParams = {
  circuitIds: string[];
  distributorIds: string[];
  circuitsToRemoveIds?: string[];
  distributorsToRemoveIds?: string[];
  shouldShareAllCurrentCircuits: boolean;
  shouldShareAllFutureCircuits: boolean;
  shouldShareCircuitsWithAllFutureDistributors?: boolean;
  networkIds?: string[];
};

export const shareCircuitsWithAgencies = async ({
  circuitIds,
  distributorIds,
  circuitsToRemoveIds = [],
  distributorsToRemoveIds = [],
  shouldShareAllCurrentCircuits,
  shouldShareAllFutureCircuits,
  shouldShareCircuitsWithAllFutureDistributors = false,
  networkIds = [],
}: ShareCircuitsParams) => {
  const result = await axios.post(apiEndpoints.distributors.shareCircuits, {
    circuitIds,
    distributorIds,
    shouldShareAllCurrentCircuits,
    shouldShareAllFutureCircuits,
    circuitsToRemoveIds,
    distributorsToRemoveIds,
    shouldShareCircuitsWithAllFutureDistributors,
    networkIds,
  });
  return result;
};

export const removeDistributor = async (distributorId: string) => {
  const result = await axios.delete(
    apiEndpoints.distributors.distributor(distributorId),
  );
  return result;
};

export const findDistributorsToShareCircuitsWith = async (
  circuitIds: string[],
) => {
  const result = await axios.post<SharedAgency[]>(
    apiEndpoints.distributors.byCircuits,
    { circuitIds },
  );
  return result.data;
};

export const findAllRecentAgenciesGroupedByNetwork = async ({
  networkId,
  networkName,
}: NetworkParams) => {
  const result = await axios.get<NetworkWithAgencies[]>(
    apiEndpoints.distributors.recent,
    {
      params: {
        networkId,
        networkName,
      },
    },
  );
  return result.data;
};

export interface AgenciesByNetworkResponse {
  agencies: Agency[];
  total: number;
  countries: { country: string }[];
  cities: { city: string }[];
}

export interface AgenciesByNetworkParams extends DistributorParams {
  recentOnly?: boolean;
}

export const findAgenciesByNetwork = async (
  networkId: string,
  {
    page = 1,
    pageSize = 10,
    sortField = '',
    sortOrder = 'asc',
    filters = {},
    recentOnly = false,
  }: AgenciesByNetworkParams,
) => {
  const filtersStr = Object.entries(filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');
  const result = await axios.get<AgenciesByNetworkResponse>(
    apiEndpoints.distributors.agencies(networkId),
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
        filters: filtersStr,
        recentOnly,
      },
    },
  );
  return result.data;
};

type ReceivedPartnershipRequestsResponse = {
  total: number;
  requests: ReceivedPartnershipRequest[];
};

export const findReceivedPartnershipRequests = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
}: PartnershipRequestsParam) => {
  const filtersStr = Object.entries({ page, pageSize, sortField, sortOrder })
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');

  const result = await axios.get<ReceivedPartnershipRequestsResponse>(
    apiEndpoints.distributors.partnershipRequests,
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
        filters: filtersStr,
      },
    },
  );
  return result.data;
};

export const answerPartnershipRequest = async ({
  distributorId,
  isAccepted,
}: {
  distributorId: string;
  isAccepted: boolean;
}) => {
  const result = await axios.post(
    apiEndpoints.distributors.partnershipRequest(distributorId),
    { isAccepted },
  );
  return result.data;
};

export type PartnerAgencyInfo = {
  User: {
    email: string;
    firstname: string;
    lastname: string;
    phoneNumber: string;
  }[];
  name: string;
  address: string | null;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  website: string | null;
  siren: string | null;
  network: {
    id: string;
    name: string;
  } | null;
};

export const getPartnerAgencyInfo = async (agencyId: string) => {
  const result = await axios.get<PartnerAgencyInfo>(
    apiEndpoints.companies.agency(agencyId),
  );
  return result.data;
};
